import React, { useState, useEffect } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import AboutModal from "../components/AboutModal";
import { getClassesAPI } from "../services/portal/admin/classService";
import Loader from "../components/Loader";
import {
  formatDate,
  formatTime,
  getDayFromDate,
  scheduleDate,
} from "../utility/Common";
import { CalendarIcon } from "../icons/CalendarIcon";
import { TimeIcon } from "../icons/TimeIcon";
import { useNavigate } from "react-router-dom";
import { RoutePaths } from "../utility/RoutePaths";
import { useAuth } from "../contexts/AuthContext";
import { getBannerAPI } from "../services/portal/admin/bannerService";

const Home = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [bannerLoading, setBannerLoading] = useState(false);
  const [bannerImage, setBannerImage] = useState(null);
  const [groupedClasses, setGroupedClasses] = useState({});

  useEffect(() => {
    loadClasses();
    loadBanner();
  }, []);

  const loadClasses = async () => {
    setLoading(true);
    try {
      const res = await getClassesAPI();
      if (res && res.IsSuccessful && res.Data) {
        // Group classes by ClassType
        const grouped = {};
        res.Data.forEach((classItem) => {
          const classTypeName = classItem.ClassType?.Name || "Other";
          if (!grouped[classTypeName]) {
            grouped[classTypeName] = [];
          }
          grouped[classTypeName].push(classItem);
        });
        setGroupedClasses(grouped);
      }
    } catch (error) {
      console.error("Error loading classes:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadBanner = async () => {
    setBannerLoading(true);
    try {
      const res = await getBannerAPI(1);
      if (res && res.IsSuccessful && res.Data) {
        const bannerData = res.Data;
        if (bannerData.Status === 1) {
          setBannerImage(bannerData.Image);
        }
      }
    } catch (error) {
      console.error("Error loading classes:", error);
    } finally {
      setBannerLoading(false);
    }
  };

  const bannerImageSrc = (value) => {
    if (!value || typeof value !== "string") return null;
    const v = value.trim();

    console.log(value);
    return `data:image/jpeg;base64,${v}`;
  };

  // Sample student results data for last 5 years
  const studentResults = [
    {
      year: "2024",
      islandRank: 1,
      districtRank: 1,
      studentName: "John Doe",
      exam: "A/L",
    },
    {
      year: "2024",
      islandRank: 3,
      districtRank: 2,
      studentName: "Jane Smith",
      exam: "A/L",
    },
    {
      year: "2023",
      islandRank: 2,
      districtRank: 1,
      studentName: "Michael Brown",
      exam: "A/L",
    },
    {
      year: "2023",
      islandRank: 5,
      districtRank: 3,
      studentName: "Sarah Johnson",
      exam: "A/L",
    },
    {
      year: "2022",
      islandRank: 1,
      districtRank: 1,
      studentName: "David Wilson",
      exam: "A/L",
    },
    {
      year: "2022",
      islandRank: 4,
      districtRank: 2,
      studentName: "Emily Davis",
      exam: "A/L",
    },
    {
      year: "2021",
      islandRank: 2,
      districtRank: 1,
      studentName: "Robert Taylor",
      exam: "A/L",
    },
    {
      year: "2021",
      islandRank: 6,
      districtRank: 3,
      studentName: "Lisa Anderson",
      exam: "A/L",
    },
    {
      year: "2020",
      islandRank: 1,
      districtRank: 1,
      studentName: "James Martinez",
      exam: "A/L",
    },
    {
      year: "2020",
      islandRank: 3,
      districtRank: 2,
      studentName: "Maria Garcia",
      exam: "A/L",
    },
  ];

  // Years for tabs (last 5 years)
  const years = ["2024", "2023", "2022", "2021", "2020"];
  const [activeYear, setActiveYear] = useState("2024");

  const openClassDetailsPage = (classId) => {
    navigate(`${RoutePaths.STUDENT_CLASS_DETAILS_PAGE}?classId=${classId}`);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* 1. Banner Section */}
      <section
        id="banner"
        className="relative bg-gradient-to-r from-black via-black-400 to-amber-500 text-white pt-24 pb-20 md:pt-32 md:pb-28"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              {/* <img src="images/logo.png" alt="royal econ" />  */}
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Excellence in Education
                <span className="block text-yellow-300">
                  Your Path to Success
                </span>
              </h1>
              <p className="text-xl md:text-2xl mb-8 text-primary-100">
                දශක ගණනාවක විශ්වාස රැඳි.. රාජකීය ආර්ථික විද්‍යා පන්තිය...
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                {!isAuthenticated && (
                  <a
                    href="/register"
                    className="bg-yellow-400 text-gray-900 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-yellow-300 transition duration-300 shadow-lg hover:shadow-xl"
                  >
                    Join Now
                  </a>
                )}
                <a
                  href="#classes"
                  className="bg-white text-primary-700 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-primary-50 transition duration-300 shadow-lg hover:shadow-xl"
                >
                  View Classes
                </a>
              </div>
            </div>
            <div className="hidden lg:block">
              <div className="relative">
                <div className="absolute inset-0 bg-primary-500 rounded-2xl transform rotate-6 opacity-20"></div>
                <div className="relative bg-yellow-200 rounded-2xl p-8 shadow-2xl">
                  <div className="space-y-4">
                    <img
                      src="images/logo.png"
                      alt="royal econ"
                      className="w-80 mx-auto"
                    />
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                      <span className="text-gray-800 font-semibold">
                        Expert Teachers
                      </span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                      <span className="text-gray-800 font-semibold">
                        Proven Results
                      </span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                      <span className="text-gray-800 font-semibold">
                        Small Class Sizes
                      </span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                      <span className="text-gray-800 font-semibold">
                        Personalized Attention
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg
            viewBox="0 0 1440 120"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z"
              fill="white"
            />
          </svg>
        </div>
      </section>

      {/* 2. About Us Section */}
      <section id="about" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              About Us
            </h2>
            <div className="w-24 h-1 bg-primary-600 mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Dedicated to nurturing academic excellence and building confident
              learners
            </p>
          </div> */}

          <div className="text-center mb-8">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              ඔබේ ගුරුතුමා...
            </h2>
            <div className="w-24 h-1 bg-primary-600 mx-auto mb-6"></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="group relative overflow-hidden rounded-lg shadow-xl cursor-pointer">
              <img
                src="images/harsha.jpg"
                alt="Tuition Class"
                className="w-full h-auto group-hover:scale-110 transition duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition duration-500 flex items-end justify-center pb-8">
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  ඔබේ ගුරුතුමා...
                </button>
              </div>
            </div>
            <div>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                ශ්‍රී ජයවර්ධනපුර විශ්ව විද්‍යාලයේ කළමනාකරණ අධ්‍යාපන හා වාණිජ
                විද්‍යා පීඨයේ විද්‍යාර්ථියෙකි. කොළඹ නාලන්දා විද්‍යාලයේ ආර්ථික
                විද්‍යා ගුරුවරයෙකු ලෙස සේවය කර ඇති අතර ගුරු ජීවිතයේ වසර 14 ක
                අත්දැකීම් සහිත ගුරුවරයෙකි. සරසවි සමත් 1500 ඉක්මවූ සිසුන්
                ඉගැන්වීමට මඟ පෙන්වූ අතර OnlineAccounting.lk ආයතනයේ ආර්ථික
                විද්‍යා විෂයේ ජ්‍යෙෂ්ඨ උපදේශකවරයෙකු ලෙස ද කටයුතු කරයි.
              </p>
              <div className="grid grid-cols-2 gap-6 mt-8">
                <div className="bg-primary-50 p-6 rounded-lg">
                  <div className="text-4xl font-bold text-primary-600 mb-2">
                    14
                  </div>
                  <div className="text-gray-700 font-semibold">
                    Years Experience
                  </div>
                </div>
                <div className="bg-green-50 p-6 rounded-lg">
                  <div className="text-4xl font-bold text-green-600 mb-2">
                    1500+
                  </div>
                  <div className="text-gray-700 font-semibold">
                    University Students
                  </div>
                </div>
                <div className="bg-yellow-50 p-6 rounded-lg">
                  <div className="text-4xl font-bold text-yellow-600 mb-2">
                    99%
                  </div>
                  <div className="text-gray-700 font-semibold">
                    Success Rate
                  </div>
                </div>
                <div className="bg-purple-50 p-6 rounded-lg">
                  <div className="text-4xl font-bold text-purple-600 mb-2">
                    10,000+
                  </div>
                  <div className="text-gray-700 font-semibold">
                    Passed Students
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {bannerImage && (
        <section className="py-10 flex justify-center">
          <img
            src={bannerImageSrc(bannerImage)}
            alt="Banner preview"
            className="max-w-full h-auto rounded-md max-h-[70vh] object-contain"
          />
        </section>
      )}

      {/* 3. Class Details and Time Tables Section */}
      <section id="classes" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Class Schedule
            </h2>
            <div className="w-24 h-1 bg-primary-600 mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Structured classes designed to maximize learning and academic
              performance
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <Loader />
            </div>
          ) : Object.keys(groupedClasses).length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600 text-lg">No classes available</p>
            </div>
          ) : (
            Object.keys(groupedClasses).map((classTypeName) => (
              <div key={classTypeName} className="mb-12">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 text-start">
                  {classTypeName}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                  {groupedClasses[classTypeName].map((classItem, index) => (
                    <div
                      key={index}
                      onClick={() => openClassDetailsPage(classItem.ClassId)}
                      className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition duration-300 border-l-4 border-primary-600 cursor-pointer"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">
                          {classItem.Name}
                        </h3>
                        <span className="text-sm font-semibold text-primary-600 bg-primary-50 px-3 py-1 rounded-full">
                          {classTypeName}
                        </span>
                      </div>
                      <div className="space-y-2 text-gray-600">
                        <div className="flex items-center">
                          <CalendarIcon />
                          <span className="font-semibold text-sm">
                            {getDayFromDate(classItem.StartDate)}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <TimeIcon />
                          <span className="font-semibold text-sm">
                            {formatTime(classItem.StartTime) +
                              " - " +
                              formatTime(classItem.EndTime)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}

          <div className="bg-white rounded-lg shadow-xl p-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8 text-primary-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                    />
                  </svg>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">
                  Theory Class
                </h4>
                <p className="text-gray-600">
                  Lorem Ipsum is simply dummy text of the printing and
                  typesetting industry.
                </p>
              </div>
              <div className="text-center">
                <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">
                  Paper Class
                </h4>
                <p className="text-gray-600">
                  Lorem Ipsum is simply dummy text of the printing and
                  typesetting industry.
                </p>
              </div>
              <div className="text-center">
                <div className="bg-yellow-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8 text-yellow-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                    />
                  </svg>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Revision</h4>
                <p className="text-gray-600">
                  Lorem Ipsum is simply dummy text of the printing and
                  typesetting industry.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. Student Results Section */}
      <section id="results" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Student Achievements
            </h2>
            <div className="w-24 h-1 bg-primary-600 mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Celebrating excellence - Our students consistently achieve top
              rankings
            </p>
          </div>

          {/* Year Tabs */}
          {/* <div className="mb-8">
            <div className="flex flex-wrap justify-center gap-2 border-b border-gray-200">
              {years.map((year) => (
                <button
                  key={year}
                  onClick={() => setActiveYear(year)}
                  className={`px-6 py-3 font-semibold text-lg transition duration-300 border-b-2 ${
                    activeYear === year
                      ? "text-primary-600 border-primary-600 bg-primary-50"
                      : "text-gray-600 border-transparent hover:text-primary-600 hover:border-gray-300"
                  }`}
                >
                  {year}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-8 shadow-lg">
            <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Academic Year {activeYear}
            </h3>
            {(() => {
              const yearResults = studentResults.filter(
                (r) => r.year === activeYear
              );
              if (yearResults.length === 0) {
                return (
                  <div className="text-center py-12">
                    <p className="text-gray-600 text-lg">
                      No results available for {activeYear}
                    </p>
                  </div>
                );
              }
              return (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {yearResults.map((result, index) => (
                    <div
                      key={index}
                      className="bg-white rounded-lg p-6 shadow-md hover:shadow-xl transition duration-300"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-2">
                          <div className="w-10 h-10 bg-yellow-400 rounded-full flex items-center justify-center">
                            <span className="text-yellow-900 font-bold text-sm">
                              {result.islandRank === 1
                                ? "🥇"
                                : result.islandRank === 2
                                ? "🥈"
                                : "🥉"}
                            </span>
                          </div>
                          <div>
                            <div className="text-sm text-gray-600">
                              Island Rank
                            </div>
                            <div className="text-2xl font-bold text-primary-600">
                              {result.islandRank}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-gray-600">
                            District Rank
                          </div>
                          <div className="text-2xl font-bold text-green-600">
                            {result.districtRank}
                          </div>
                        </div>
                      </div>
                      <div className="border-t pt-4">
                        <div className="font-semibold text-gray-900 mb-1">
                          {result.studentName}
                        </div>
                        <div className="text-sm text-gray-600">
                          {result.exam} Examination
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div> */}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
            <div className="bg-primary-50 p-6 rounded-lg">
              <div className="text-4xl font-bold text-primary-600 mb-2">14</div>
              <div className="text-gray-700 font-semibold">
                Years Experience
              </div>
            </div>
            <div className="bg-green-50 p-6 rounded-lg">
              <div className="text-4xl font-bold text-green-600 mb-2">
                1500+
              </div>
              <div className="text-gray-700 font-semibold">
                University Students
              </div>
            </div>
            <div className="bg-yellow-50 p-6 rounded-lg">
              <div className="text-4xl font-bold text-yellow-600 mb-2">99%</div>
              <div className="text-gray-700 font-semibold">Success Rate</div>
            </div>
            <div className="bg-purple-50 p-6 rounded-lg">
              <div className="text-4xl font-bold text-purple-600 mb-2">
                10,000+
              </div>
              <div className="text-gray-700 font-semibold">Passed Students</div>
            </div>
          </div>

          {/* Summary Statistics */}
          {/* <div className="mt-16 bg-primary-600 rounded-lg p-8 text-white">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 text-center">
              <div>
                <div className="text-4xl font-bold mb-2">20+</div>
                <div className="text-primary-100">Island Top 10 Rankings</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">35+</div>
                <div className="text-primary-100">District Top 10 Rankings</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">100+</div>
                <div className="text-primary-100">University Entrants</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">98%</div>
                <div className="text-primary-100">Pass Rate</div>
              </div>
            </div>
          </div> */}
        </div>
      </section>

      {/* 5. Image Gallery Section */}
      <section
        id="gallery"
        className="py-20 bg-gradient-to-br from-gray-50 to-blue-50"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Gallery
            </h2>
            <div className="w-24 h-1 bg-primary-600 mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Take a look at our classes, events, and moments that showcase our
              commitment to excellence
            </p>
          </div>

          {/* Collage Style Gallery */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 auto-rows-fr">
            {/* Large Featured Image - Top Left */}
            <div className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:scale-105 col-span-2 row-span-2 md:col-span-2 md:row-span-2">
              <img
                src="images/gallery/1.jpg"
                alt="Royal Econ Class"
                className="w-full h-full object-cover min-h-[400px] md:min-h-[500px] group-hover:scale-110 transition duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition duration-500">
                <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                  <h3 className="text-2xl md:text-3xl font-bold mb-2">
                    Our Classes
                  </h3>
                  <p className="text-base md:text-lg text-white/90 mb-4">
                    Interactive learning environment
                  </p>
                </div>
              </div>
            </div>

            {/* Medium Image - Top Right */}
            <div className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:scale-105 col-span-2 md:col-span-2 row-span-1 md:row-span-1">
              <img
                src="images/gallery/2.jpg"
                alt="Expert Teacher"
                className="w-full h-full object-cover min-h-[200px] md:min-h-[240px] group-hover:scale-110 transition duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition duration-500">
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <h3 className="text-xl md:text-2xl font-bold mb-2">
                    Expert Teaching
                  </h3>
                  <p className="text-sm md:text-base text-white/90">
                    Dedicated and experienced educators
                  </p>
                </div>
              </div>
            </div>

            {/* Small Square Image - Bottom Right */}
            <div className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:scale-105 col-span-1 md:col-span-1 row-span-1">
              <div className="w-full h-full bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center min-h-[200px] md:min-h-[240px]">
                <img
                  src="images/gallery/3.jpg"
                  alt="Royal Econ Logo"
                  className="object-contain group-hover:scale-110 transition duration-700"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition duration-500">
                <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                  <h3 className="text-lg font-bold mb-1">Royal Econ</h3>
                  <p className="text-xs text-white/90">
                    Excellence in Education
                  </p>
                </div>
              </div>
            </div>

            {/* Medium Vertical Image - Bottom Right */}
            <div className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:scale-105 col-span-1 md:col-span-1 row-span-1">
              <div className="w-full h-full bg-gradient-to-br from-indigo-100 via-blue-100 to-purple-100 flex items-center justify-center min-h-[200px] md:min-h-[240px]">
                <img
                  src="images/gallery/1.jpg"
                  alt="Royal Econ"
                  className="object-contain group-hover:scale-110 transition duration-700"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition duration-500">
                <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                  <h3 className="text-lg font-bold mb-1">Quality Education</h3>
                  <p className="text-xs text-white/90">Since 2009</p>
                </div>
              </div>
            </div>

            {/* Placeholder for future images - Add more collage items here */}
            {/* 
            <div className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:scale-105 col-span-1 md:col-span-2">
              <img
                src="images/your-image.jpg"
                alt="Gallery Image"
                className="w-full h-full object-cover min-h-[200px] group-hover:scale-110 transition duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition duration-500">
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <h3 className="text-xl font-bold mb-2">Image Title</h3>
                  <p className="text-sm text-white/90">Image description</p>
                </div>
              </div>
            </div>
            */}
          </div>
        </div>
      </section>

      {/* 6. Contact Us Section */}
      <section
        id="contact"
        className="py-20 bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Contact Us
            </h2>
            <div className="w-24 h-1 bg-primary-600 mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Get in touch with us to learn more about our classes and
              enrollment. We're here to help you achieve your academic goals.
            </p>
          </div>

          {/* Contact Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {/* Phone Card */}
            <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-2xl transition duration-300 transform hover:-translate-y-2 border-t-4 border-primary-600">
              <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg
                  className="w-8 h-8 text-primary-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                Phone
              </h3>
              <div className="space-y-2 text-center">
                <a
                  href="tel:+94712227623"
                  className="block text-primary-600 hover:text-primary-700 font-semibold text-lg transition duration-300"
                >
                  +94 77 474 7623
                </a>
                <a
                  href="tel:+94774747623"
                  className="block text-primary-600 hover:text-primary-700 font-semibold text-lg transition duration-300"
                >
                  +94 71 222 7623
                </a>
              </div>
              <p className="text-gray-600 text-sm text-center mt-4">Call us</p>
            </div>

            {/* Email Card */}
            <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-2xl transition duration-300 transform hover:-translate-y-2 border-t-4 border-green-600">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg
                  className="w-8 h-8 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                Email
              </h3>
              <div className="text-center">
                <a
                  href="mailto:royaleconpvtltd@gmail.com"
                  className="text-green-600 hover:text-green-700 font-semibold text-lg break-all transition duration-300"
                >
                  royaleconpvtltd@gmail.com
                </a>
              </div>
              <p className="text-gray-600 text-sm text-center mt-4">
                Send us an email anytime
              </p>
            </div>

            {/* Address Card */}
            <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-2xl transition duration-300 transform hover:-translate-y-2 border-t-4 border-yellow-600 md:col-span-2 lg:col-span-1">
              <div className="bg-yellow-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg
                  className="w-8 h-8 text-yellow-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                Address
              </h3>
              <div className="text-center">
                <p className="text-gray-700 font-medium leading-relaxed">
                  No: 494/1,
                  <br />
                  Kitulhena,
                  <br />
                  Mattegoda,
                  <br />
                  Kottawa
                </p>
              </div>
              <p className="text-gray-600 text-sm text-center mt-4">
                Visit us at our location
              </p>
            </div>
          </div>

          {/* Additional Information Section */}
          <div className="bg-white rounded-xl shadow-xl p-8 md:p-12">
            <div className="max-w-4xl mx-auto">
              <h3 className="text-3xl font-bold text-gray-900 mb-6 text-center">
                අපි ඔබට සහාය වීමට සූදානම්
              </h3>
              <p className="text-lg text-gray-700 text-center leading-relaxed mb-8">
                අපගේ පාඨමාලා, බඳවා ගැනීමේ ක්‍රියාවලිය පිළිබඳව ඔබට කිසියම්
                ගැටලුවක් ඇත්නම් හෝ අපගේ ඉගැන්වීම් ක්‍රමවේදයන් පිළිබඳ වැඩිදුර
                තොරතුරු අවශ්‍ය නම්, ඔබට සහාය වීමට අපගේ කණ්ඩායම සූදානම්. ඉහත
                සඳහන් ඕනෑම සම්බන්ධතා ක්‍රමයක් හරහා අප හා සම්බන්ධ වන්න, එවිට අපි
                හැකි ඉක්මනින් ඔබව සම්බන්ධ කර ගන්නෙමු.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary-600 mb-2">
                    24/7
                  </div>
                  <div className="text-gray-700 font-semibold">
                    Hotline Support
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    Quick
                  </div>
                  <div className="text-gray-700 font-semibold">
                    Response Time
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-yellow-600 mb-2">
                    Expert
                  </div>
                  <div className="text-gray-700 font-semibold">Guidance</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6. Footer Section */}
      <Footer />

      {/* About Harsha Modal */}
      {isModalOpen && <AboutModal setIsModalOpen={setIsModalOpen} />}
    </div>
  );
};

export default Home;
